import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import useRegistrationData from '../../hooks/useRegistrationData'
import useProfileCompletionCheck from '../../hooks/useProfileCompletionCheck'
import useOnlineStatus from '../../hooks/useOnlineStatus'
import PageLayout from '../common/layout/PageLayout'
import LoadingSpinner from '../common/LoadingSpinner'
import InlineProfileCompletionModal from '../auth/InlineProfileCompletionModal'
import SelectCourtStep from './registration/SelectCourtStep'
import SelectLevelStep from './registration/SelectLevelStep'
import SelectSkillLevelStep from './registration/SelectSkillLevelStep'
import PaymentStep from './registration/PaymentStep'
import ReviewStep from './registration/ReviewStep'
import CompleteStep from './registration/CompleteStep'
import StepIndicator from './registration/StepIndicator'
import { isValidSkillLevel } from '../../utils/validateSkillLevel'
import { track } from '../../analytics'
import toastr from 'toastr'
import ApiLeagues from '../../api/ApiLeagues'
import StorageManager, { STORAGE_PRIORITY } from '../../utils/storageManager'
import './Register.css'
import './registration/MobileOptimizations.css'

const STEPS = {
  LEVEL: 1,
  SKILL_LEVEL: 2,
  COURT: 3,
  PAYMENT: 4,
  REVIEW: 5,
  COMPLETE: 6,
}

// Step sequences (excluding COMPLETE) — order differs for doubles
const SINGLES_SEQUENCE = [
  STEPS.LEVEL,
  STEPS.SKILL_LEVEL,
  STEPS.COURT,
  STEPS.PAYMENT,
  STEPS.REVIEW,
  STEPS.COMPLETE,
]
const DOUBLES_SEQUENCE = [
  STEPS.LEVEL,
  STEPS.COURT,
  STEPS.SKILL_LEVEL,
  STEPS.PAYMENT,
  STEPS.REVIEW,
  STEPS.COMPLETE,
]

// Number of steps shown in the progress indicator (excludes COMPLETE)
const VISIBLE_STEPS = 5

function RegistrationFlow() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const {
    primaryPlayer,
    leagues,
    myLeagues,
    setMyLeagues,
    loading,
    error,
    loadData,
  } = useRegistrationData()
  const { showProfileModal, clearSkip } = useProfileCompletionCheck(
    primaryPlayer,
    user,
    { respectSkip: false, requireSkillLevel: false },
  )
  const isOnline = useOnlineStatus()

  // Parse step from URL or default to LEVEL
  const urlStep = parseInt(searchParams.get('step') || '1', 10)
  const initialStep =
    urlStep >= STEPS.LEVEL && urlStep <= STEPS.COMPLETE ? urlStep : STEPS.LEVEL

  const [currentStep, setCurrentStep] = useState(initialStep)
  const stepContainerRef = useRef(null)

  // Restore court and league from localStorage (changed from sessionStorage for better persistence)
  const getInitialCourt = () => {
    try {
      const storedCourt = StorageManager.get('registrationCourt')
      if (storedCourt) {
        return JSON.parse(storedCourt)
      }
    } catch (e) {
      console.error('Failed to parse stored court', e)
    }
    return null
  }

  const getInitialLeagueId = () => {
    try {
      const storedLeagueId = StorageManager.get('registrationLeague')
      return storedLeagueId || ''
    } catch (e) {
      console.error('Failed to parse stored league', e)
      return ''
    }
  }

  const getInitialSkillLevel = () => {
    try {
      const storedSkillLevel = StorageManager.get('registrationSkillLevel')
      return storedSkillLevel ? parseFloat(storedSkillLevel) : null
    } catch (e) {
      console.error('Failed to parse stored skill level', e)
      return null
    }
  }

  const getInitialAccessCode = () => {
    try {
      const storedAccessCode = StorageManager.get('registrationAccessCode')
      return storedAccessCode || null
    } catch (e) {
      console.error('Failed to parse stored access code', e)
      return null
    }
  }

  const getInitialNominatedPartner = () => {
    try {
      const stored = StorageManager.get('registrationPartner')
      if (!stored) return null
      const parsed = JSON.parse(stored)
      if (parsed && parsed.type) return parsed
      // Legacy: handle old integer format gracefully
      const legacyId = parseInt(stored, 10)
      return Number.isInteger(legacyId)
        ? { type: 'player', id: legacyId, label: '' }
        : null
    } catch (e) {
      console.error('Failed to parse stored partner', e)
      return null
    }
  }

  const [selectedCourt, setSelectedCourt] = useState(getInitialCourt())
  const [selectedLeagueId, setSelectedLeagueId] = useState(getInitialLeagueId())
  const [selectedSkillLevel, setSelectedSkillLevel] = useState(
    getInitialSkillLevel(),
  )
  const [validatedAccessCode, setValidatedAccessCode] = useState(
    getInitialAccessCode(),
  )
  const [nominatedPartner, setNominatedPartner] = useState(
    getInitialNominatedPartner(),
  )
  const [registeredLeagueName, setRegisteredLeagueName] = useState('')
  const [, setRegisteredLeagueId] = useState(null)
  const [activeLeagueLevels, setActiveLeagueLevels] = useState([])
  const [isLeagueLevelsLoading, setIsLeagueLevelsLoading] = useState(false)

  const selectedLeague = useMemo(
    () => leagues.find((l) => l.id === parseInt(selectedLeagueId, 10)) || null,
    [leagues, selectedLeagueId],
  )
  const hasResolvedSelectedLeague = !selectedLeagueId || Boolean(selectedLeague)
  const isDoublesRegistration = selectedLeague?.league_type === 'Doubles'

  // Compute whether payment is required (used for conditional navigation)
  const requiresPayment = useMemo(() => {
    return (
      selectedLeague && selectedLeague.entry_fee > 0 && !validatedAccessCode
    )
  }, [selectedLeague, validatedAccessCode])

  // Persist court selection to localStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedCourt) {
        StorageManager.set(
          'registrationCourt',
          JSON.stringify(selectedCourt),
          STORAGE_PRIORITY.HIGH,
        )
      } else {
        StorageManager.remove('registrationCourt')
      }
    } catch (error) {
      // Swallow storage errors to avoid breaking registration flow
      // (e.g., private browsing mode, quota exceeded)
      console.error('Failed to persist court selection to localStorage', error)
    }
  }, [selectedCourt])

  // Persist league selection to localStorage whenever it changes
  useEffect(() => {
    try {
      if (selectedLeagueId) {
        StorageManager.set(
          'registrationLeague',
          selectedLeagueId,
          STORAGE_PRIORITY.HIGH,
        )
      } else {
        StorageManager.remove('registrationLeague')
      }
    } catch (error) {
      // Swallow storage errors to avoid breaking registration flow
      // (e.g., private browsing mode, quota exceeded)
      console.error('Failed to persist league selection to localStorage', error)
    }
  }, [selectedLeagueId])

  // Persist skill level selection to localStorage whenever it changes
  useEffect(() => {
    try {
      if (isValidSkillLevel(selectedSkillLevel)) {
        StorageManager.set(
          'registrationSkillLevel',
          selectedSkillLevel.toString(),
          STORAGE_PRIORITY.HIGH,
        )
      } else {
        StorageManager.remove('registrationSkillLevel')
      }
    } catch (error) {
      console.error(
        'Failed to persist skill level selection to localStorage',
        error,
      )
    }
  }, [selectedSkillLevel])

  // Persist access code to localStorage whenever it changes
  useEffect(() => {
    try {
      if (validatedAccessCode) {
        StorageManager.set(
          'registrationAccessCode',
          validatedAccessCode,
          STORAGE_PRIORITY.HIGH,
        )
      } else {
        StorageManager.remove('registrationAccessCode')
      }
    } catch (error) {
      console.error('Failed to persist access code to localStorage', error)
    }
  }, [validatedAccessCode])

  // Persist nominated partner to localStorage whenever it changes
  useEffect(() => {
    try {
      if (nominatedPartner) {
        StorageManager.set(
          'registrationPartner',
          JSON.stringify(nominatedPartner),
          STORAGE_PRIORITY.HIGH,
        )
      } else {
        StorageManager.remove('registrationPartner')
      }
    } catch (error) {
      console.error(
        'Failed to persist partner selection to localStorage',
        error,
      )
    }
  }, [nominatedPartner])

  // Sync step with URL (keep only step parameter)
  useEffect(() => {
    const params = { step: currentStep.toString() }

    // Avoid unnecessary URL updates by comparing with current params
    const currentParams = new URLSearchParams(window.location.search)
    const nextParams = new URLSearchParams(params)

    if (currentParams.toString() !== nextParams.toString()) {
      setSearchParams(params, { replace: true })
    }
  }, [currentStep, setSearchParams])

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      // Read from window.location.search directly to avoid stale searchParams closure
      const params = new URLSearchParams(window.location.search)
      const step = parseInt(params.get('step') || '1', 10)
      if (step >= STEPS.LEVEL && step <= STEPS.COMPLETE) {
        setCurrentStep(step)
      }
      // Restore court and league from localStorage
      try {
        const storedCourt = StorageManager.get('registrationCourt')
        if (storedCourt) {
          setSelectedCourt(JSON.parse(storedCourt))
        } else {
          setSelectedCourt(null)
        }
      } catch (e) {
        console.error('Failed to restore court on popstate', e)
        setSelectedCourt(null)
      }
      try {
        const storedLeagueId = StorageManager.get('registrationLeague')
        setSelectedLeagueId(storedLeagueId || '')
      } catch (e) {
        console.error('Failed to restore league on popstate', e)
        setSelectedLeagueId('')
      }
      try {
        const storedSkillLevel = StorageManager.get('registrationSkillLevel')
        setSelectedSkillLevel(
          storedSkillLevel ? parseFloat(storedSkillLevel) : null,
        )
      } catch (e) {
        console.error('Failed to restore skill level on popstate', e)
        setSelectedSkillLevel(null)
      }
      try {
        const storedAccessCode = StorageManager.get('registrationAccessCode')
        setValidatedAccessCode(storedAccessCode || null)
      } catch (e) {
        console.error('Failed to restore access code on popstate', e)
        setValidatedAccessCode(null)
      }
      try {
        const storedPartnerId = StorageManager.get('registrationPartner')
        if (storedPartnerId) {
          try {
            const parsed = JSON.parse(storedPartnerId)
            setNominatedPartner(parsed?.type ? parsed : null)
          } catch {
            setNominatedPartner(null)
          }
        } else {
          setNominatedPartner(null)
        }
      } catch (e) {
        console.error('Failed to restore partner on popstate', e)
        setNominatedPartner(null)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (!hasResolvedSelectedLeague) return

    if (!isDoublesRegistration && nominatedPartner) {
      setNominatedPartner(null)
      StorageManager.remove('registrationPartner')
    }
  }, [hasResolvedSelectedLeague, isDoublesRegistration, nominatedPartner])

  useEffect(() => {
    let cancelled = false

    if (!selectedLeague) {
      setActiveLeagueLevels([])
      setIsLeagueLevelsLoading(false)
      return () => {
        cancelled = true
      }
    }

    const loadLeagueLevels = async () => {
      setIsLeagueLevelsLoading(true)
      try {
        const response = await ApiLeagues.getLeagueLevels(selectedLeague.id)
        const levels = Array.isArray(response?.league_levels)
          ? response.league_levels
          : []
        if (!cancelled) {
          const filtered = levels.filter(
            (leagueLevel) => leagueLevel.status === 'active',
          )
          setActiveLeagueLevels(filtered)
          // If the previously-selected skill level is not available in the new
          // league's levels, clear it so the user is not silently advanced with
          // an invalid selection. Only clear when the league has an explicit
          // level filter (filtered.length > 0); legacy leagues with no levels
          // allow any skill level.
          if (
            filtered.length > 0 &&
            isValidSkillLevel(selectedSkillLevel) &&
            !filtered.some(
              (ll) => Number(ll.skill_level) === selectedSkillLevel,
            )
          ) {
            setSelectedSkillLevel(null)
          }
        }
      } catch {
        if (!cancelled) {
          setActiveLeagueLevels([])
        }
      } finally {
        if (!cancelled) {
          setIsLeagueLevelsLoading(false)
        }
      }
    }

    loadLeagueLevels()

    return () => {
      cancelled = true
    }
    // selectedSkillLevel is intentionally excluded: we only want this reset
    // to run when the league changes, not every time the skill level changes.
    // selectedLeague object is intentionally excluded in favour of its id to
    // avoid spurious re-fetches when the same league's reference is replaced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeague?.id])

  // Validate that user has required state for current step
  useEffect(() => {
    // Navigation guard: Redirect from PAYMENT step if league is free or has valid access code
    if (currentStep === STEPS.PAYMENT && !requiresPayment) {
      // Redirect to REVIEW if payment is not required
      setCurrentStep(STEPS.REVIEW)
    }

    // Redirect to step 1 if navigating to review without selections
    if (currentStep === STEPS.REVIEW) {
      if (
        !selectedCourt ||
        !selectedLeagueId ||
        !isValidSkillLevel(selectedSkillLevel)
      ) {
        setCurrentStep(STEPS.LEVEL)
      }
    }

    // Redirect to step 1 if navigating to complete without registration
    if (currentStep === STEPS.COMPLETE && !registeredLeagueName) {
      setCurrentStep(STEPS.LEVEL)
    }
  }, [
    currentStep,
    selectedCourt,
    selectedLeagueId,
    selectedSkillLevel,
    requiresPayment,
    registeredLeagueName,
  ])

  const isLeagueRegistered = (leagueId) => {
    const parsedId = parseInt(leagueId, 10)
    return myLeagues.includes(parsedId)
  }

  const getStepName = (step) => {
    switch (step) {
      case STEPS.LEVEL:
        return 'select_league'
      case STEPS.SKILL_LEVEL:
        return 'select_skill_level'
      case STEPS.COURT:
        return 'select_court'
      case STEPS.PAYMENT:
        return 'payment'
      case STEPS.REVIEW:
        return 'review'
      case STEPS.COMPLETE:
        return 'complete'
      default:
        return 'unknown'
    }
  }

  // Track step views when currentStep changes
  useEffect(() => {
    if (currentStep === STEPS.COMPLETE) {
      // Complete step tracking is handled in CompleteStep component
      return
    }

    try {
      track('registration_step_viewed', {
        step: currentStep,
        step_name: getStepName(currentStep),
      })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }
  }, [currentStep])

  const handleNext = () => {
    if (currentStep === STEPS.COMPLETE) return
    const sequence = isDoublesRegistration ? DOUBLES_SEQUENCE : SINGLES_SEQUENCE
    const idx = sequence.indexOf(currentStep)
    if (idx === -1 || idx >= sequence.length - 1) return

    let nextStep = sequence[idx + 1]

    // Skip PAYMENT step for free leagues or when access code is valid
    if (nextStep === STEPS.PAYMENT && !requiresPayment) {
      nextStep = sequence[idx + 2] // REVIEW
    }

    // Track step completion
    try {
      track('registration_step_completed', {
        from_step: currentStep,
        from_step_name: getStepName(currentStep),
        to_step: nextStep,
        to_step_name: getStepName(nextStep),
      })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }

    setCurrentStep(nextStep)
  }

  // Focus management: move focus to step container when step changes
  useEffect(() => {
    if (stepContainerRef.current) {
      stepContainerRef.current.focus()
    }
  }, [currentStep])

  const handleBack = () => {
    const sequence = isDoublesRegistration ? DOUBLES_SEQUENCE : SINGLES_SEQUENCE
    const idx = sequence.indexOf(currentStep)
    if (idx <= 0) return

    const prevStep = sequence[idx - 1]

    // Track back button click
    try {
      track('registration_step_back', {
        from_step: currentStep,
        from_step_name: getStepName(currentStep),
        to_step: prevStep,
        to_step_name: getStepName(prevStep),
      })
    } catch (err) {
      console.error('Analytics tracking error:', err)
    }

    setCurrentStep(prevStep)
  }

  const handleBackSteps = (stepsBack) => {
    const sequence = isDoublesRegistration ? DOUBLES_SEQUENCE : SINGLES_SEQUENCE
    const idx = sequence.indexOf(currentStep)
    const newIdx = Math.max(0, idx - stepsBack)
    setCurrentStep(sequence[newIdx])
  }

  const handleGoToStep = (targetStep) => {
    setCurrentStep(targetStep)
  }

  const handleComplete = (leagueName, leagueId) => {
    setRegisteredLeagueName(leagueName)
    setRegisteredLeagueId(leagueId || parseInt(selectedLeagueId, 10))
    setCurrentStep(STEPS.COMPLETE)
    // Clear localStorage after successful registration
    StorageManager.remove('registrationCourt')
    StorageManager.remove('registrationLeague')
    StorageManager.remove('registrationSkillLevel')
    StorageManager.remove('registrationAccessCode')
    StorageManager.remove('registrationPartner')
  }

  const handleReset = () => {
    setCurrentStep(STEPS.LEVEL)
    setSelectedCourt(null)
    setSelectedLeagueId('')
    setSelectedSkillLevel(null)
    setValidatedAccessCode(null)
    setNominatedPartner(null)
    setRegisteredLeagueName('')
    setRegisteredLeagueId(null)
    // Clear localStorage on reset
    StorageManager.remove('registrationCourt')
    StorageManager.remove('registrationLeague')
    StorageManager.remove('registrationSkillLevel')
    StorageManager.remove('registrationAccessCode')
    StorageManager.remove('registrationPartner')
  }

  if (loading) {
    return (
      <PageLayout title="Register for a League">
        <div className="content-card">
          <LoadingSpinner
            message="Loading registration data..."
            aria-live="polite"
          />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout title="Register for a League">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Registration</h4>
          <p>{error}</p>
          <hr />
          <button className="btn btn-primary" onClick={() => loadData()}>
            Retry
          </button>
        </div>
      </PageLayout>
    )
  }

  if (!primaryPlayer) {
    return (
      <PageLayout title="Register for a League">
        <div className="content-card">
          <div className="player-info">
            Please complete your player profile first.
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Register for a League">
      {/* Skip link for screen readers */}
      <a
        href="#registration-form"
        className="visually-hidden-focusable position-absolute top-0 start-0 m-3 p-2 bg-primary text-white"
        style={{ zIndex: 9999 }}
      >
        Skip to registration form
      </a>

      <div className="content-card">
        {!isOnline && (
          <div className="alert alert-warning mb-3" role="alert">
            <strong>No internet connection.</strong> Please check your network
            connection. Some features may not work properly.
          </div>
        )}

        <div className="player-info">
          <div className="player-header">
            <h3>Welcome, {primaryPlayer.first_name || 'there'}! 👋</h3>
            <p className="player-subtext">Ready to join a new league?</p>
          </div>
        </div>

        <StepIndicator
          currentStep={
            currentStep === STEPS.COMPLETE
              ? VISIBLE_STEPS
              : (isDoublesRegistration
                  ? DOUBLES_SEQUENCE
                  : SINGLES_SEQUENCE
                ).indexOf(currentStep) + 1
          }
          totalSteps={VISIBLE_STEPS}
          showCompleted={currentStep === STEPS.COMPLETE}
          stepLabels={
            isDoublesRegistration
              ? [
                  'League',
                  'Court & Partner',
                  'Skill Level',
                  'Payment',
                  'Review',
                ]
              : undefined
          }
        />

        {/* Main registration form container with focus management */}
        <div
          id="registration-form"
          ref={stepContainerRef}
          tabIndex={-1}
          aria-label="Registration form"
        >
          {currentStep === STEPS.LEVEL && (
            <SelectLevelStep
              leagues={leagues}
              selectedLeagueId={selectedLeagueId}
              onSelectLeague={setSelectedLeagueId}
              isLeagueRegistered={isLeagueRegistered}
              onNext={handleNext}
            />
          )}

          {currentStep === STEPS.SKILL_LEVEL && (
            <SelectSkillLevelStep
              selectedSkillLevel={selectedSkillLevel}
              onSelectSkillLevel={setSelectedSkillLevel}
              onNext={handleNext}
              onBack={handleBack}
              isDoublesRegistration={isDoublesRegistration}
              requiresPayment={requiresPayment}
              activeLeagueLevels={activeLeagueLevels}
              isLoadingLeagueLevels={isLeagueLevelsLoading}
              partnerSkillLevel={
                isDoublesRegistration
                  ? (nominatedPartner?.doubles_skill_level ?? null)
                  : null
              }
            />
          )}

          {currentStep === STEPS.COURT && (
            <SelectCourtStep
              selectedCourt={selectedCourt}
              onSelectCourt={setSelectedCourt}
              onNext={handleNext}
              onBack={handleBack}
              validatedAccessCode={validatedAccessCode}
              onAccessCodeValidated={setValidatedAccessCode}
              selectedLeague={selectedLeague}
              primaryPlayer={primaryPlayer}
              nominatedPartner={nominatedPartner}
              onSelectPartner={setNominatedPartner}
            />
          )}

          {currentStep === STEPS.PAYMENT && (
            <PaymentStep
              selectedLeague={selectedLeague}
              primaryPlayer={primaryPlayer}
              selectedSkillLevel={selectedSkillLevel}
              nominatedPartner={nominatedPartner}
              validatedAccessCode={validatedAccessCode}
              stepNumber={STEPS.PAYMENT}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === STEPS.REVIEW && (
            <ReviewStep
              selectedCourt={selectedCourt}
              selectedLeagueId={selectedLeagueId}
              selectedSkillLevel={selectedSkillLevel}
              nominatedPartner={nominatedPartner}
              validatedAccessCode={validatedAccessCode}
              leagues={leagues}
              primaryPlayer={primaryPlayer}
              myLeagues={myLeagues}
              setMyLeagues={setMyLeagues}
              onBack={handleBack}
              onBackSteps={handleBackSteps}
              onGoToStep={handleGoToStep}
              onComplete={handleComplete}
              showProfileModal={showProfileModal}
            />
          )}

          {currentStep === STEPS.COMPLETE && (
            <CompleteStep
              leagueName={registeredLeagueName}
              nominatedPartner={isDoublesRegistration ? nominatedPartner : null}
              onReset={handleReset}
              onViewLeagues={() => navigate('/my-leagues')}
              onViewDashboard={() => navigate('/')}
            />
          )}
        </div>
      </div>

      <InlineProfileCompletionModal
        show={showProfileModal}
        onComplete={() => {
          try {
            clearSkip()
          } catch (error) {
            console.error('Failed to clear skip flag:', error)
          }
          toastr.success(
            'Welcome to Serve Sync Tennis! Your profile is now complete.',
          )
          window.location.reload()
        }}
        onSkip={() => {}}
        allowSkip={false}
        requireSkillLevel={false}
        primaryPlayer={primaryPlayer}
        user={user}
      />
    </PageLayout>
  )
}

export default RegistrationFlow
